import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowRight, PenTool, Wrench, Cog,
  Trophy, Users, BookOpen, Building2, Hammer, Compass,
  ChevronDown, CheckCircle2, Send, Award,
  GraduationCap, Rocket, Printer,
  Shield, Brain
} from 'lucide-react';
import { useBotShield } from '../../hooks/useBotShield';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INTEREST_SOURCE = 'engineering_interest';

/* ───── Contact Form ───── */
function InlineContactForm({ program }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', studentGrade: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { HoneypotField, getPayloadFields } = useBotShield();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) { toast.error('Name and email required.'); return; }
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name, email: formData.email, phone: formData.phone,
          subject: `${program} Interest`,
          message: `Program: ${program}\nGrade: ${formData.studentGrade}`,
          source: INTEREST_SOURCE,
          ...getPayloadFields(),
        }),
      });
    } catch {}
    setSubmitted(true); setSubmitting(false);
    toast.success("We received your interest! We'll be in touch soon.");
  };

  if (submitted) return (
    <div className="text-center py-3">
      <CheckCircle2 className="w-7 h-7 text-emerald-500 mx-auto mb-1" />
      <p className="text-xs font-semibold text-gray-700">Received! We'll be in touch.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <HoneypotField />
      <div className="grid grid-cols-2 gap-2">
        <Input className="text-sm h-8" placeholder="Name *" value={formData.name} onChange={e => setFormData(p=>({...p, name: e.target.value}))} required />
        <Input className="text-sm h-8" type="email" placeholder="Email *" value={formData.email} onChange={e => setFormData(p=>({...p, email: e.target.value}))} required />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input className="text-sm h-8" placeholder="Phone" value={formData.phone} onChange={e => setFormData(p=>({...p, phone: e.target.value}))} />
        <Input className="text-sm h-8" placeholder="Student Grade" value={formData.studentGrade} onChange={e => setFormData(p=>({...p, studentGrade: e.target.value}))} />
      </div>
      <Button type="submit" disabled={submitting} className="w-full bg-rsc-yellow text-rsc-navy font-bold rounded-full h-8 text-xs">
        {submitting ? 'Sending...' : <><Send className="w-3 h-3 mr-1" /> Express Interest</>}
      </Button>
    </form>
  );
}

/* ───── Career Data ───── */
const CAREER_PATHS = [
  { title: 'Mechanical Engineer', salary: '$95K+', icon: Cog, prep: 'CAD, Mechanical Systems, Physics, Math' },
  { title: 'Aerospace Engineer', salary: '$122K+', icon: Rocket, prep: 'CAD, Physics, Advanced Math, Design' },
  { title: 'Civil Engineer', salary: '$95K+', icon: Building2, prep: 'Structural, CAD, Materials, Math' },
  { title: 'Architect', salary: '$93K+', icon: Compass, prep: 'CAD, Spatial Design, Sustainability' },
  { title: 'Industrial Designer', salary: '$80K+', icon: PenTool, prep: '3D Printing, CAD, Fabrication' },
  { title: 'Biomedical Engineer', salary: '$100K+', icon: Brain, prep: 'CAD, Mechanical, Biology, Chemistry' },
];

const HERO_STATS = [
  { value: '7', label: 'Engineering Programs', icon: Wrench },
  { value: '3D', label: 'Prints Shipped Home', icon: Printer },
  { value: 'Top 5%', label: 'STEM.org Accredited', icon: Award },
  { value: 'Gr 3–12', label: 'All Grade Levels', icon: Users },
];

/* ───── Main Page ───── */
export default function EngineeringDivisionPage() {

  return (
    <div data-testid="engineering-division-page">

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative min-h-[78vh] flex items-center overflow-hidden bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847]">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute top-20 -right-40 w-[500px] h-[500px] bg-orange-500/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-10 -left-40 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]" />

        <div className="container-app relative z-10 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-sm font-medium mb-4" data-testid="hero-badge">
                <Wrench className="w-4 h-4" /> A Ready Set STEM Division
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-4 tracking-tight">
                Design. Build.<br />
                <span className="text-rsc-yellow">Engineer the Future.</span>
              </h1>
              <p className="text-base sm:text-lg text-white/60 max-w-xl mb-7 leading-relaxed">
                CAD design, 3D printing, mechanical engineering, bridge building, and architecture —
                our engineering division turns students into makers and problem-solvers who can design
                and build real things. Every student gets their 3D prints shipped home.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Link to="/catalog?category=engineering">
                  <Button className="bg-rsc-yellow text-rsc-navy text-sm px-7 py-5 font-bold rounded-full shadow-lg shadow-rsc-yellow/20 hover:shadow-rsc-yellow/30 hover:scale-[1.02] transition-all" data-testid="explore-courses-btn">
                    Explore Engineering Courses <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hero-stats">
                {HERO_STATS.map((stat, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white/[0.06] border border-white/[0.08]">
                    <stat.icon className="w-4 h-4 text-rsc-yellow/60 mb-1" />
                    <p className="text-xl font-extrabold text-white leading-none">{stat.value}</p>
                    <p className="text-[11px] text-white/40 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:grid grid-cols-2 gap-3">
              <div className="space-y-3">
                <div className="rounded-xl overflow-hidden aspect-[4/3]">
                  <img src="https://images.unsplash.com/photo-1725245222829-87949b74d042?w=400&auto=format" alt="Kids engineering" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/5]">
                  <img src="https://images.pexels.com/photos/4705622/pexels-photo-4705622.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Engineering project" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
              <div className="space-y-3 pt-6">
                <div className="rounded-xl overflow-hidden aspect-[4/5]">
                  <img src="https://images.pexels.com/photos/4705612/pexels-photo-4705612.jpeg?auto=compress&cs=tinysrgb&w=400" alt="Building structures" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/3]">
                  <img src="https://images.unsplash.com/photo-1614898929027-04c0152069d1?w=400&auto=format" alt="3D printing" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-5 h-5 text-white/20" />
        </div>
      </section>

      {/* ═══════════ CAREER OUTCOMES ═══════════ */}
      <section className="relative py-12 bg-gray-100" data-testid="career-section">
        <div className="container-app">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-navy/20 bg-rsc-navy/5 text-rsc-navy text-xs font-medium mb-3">
              <GraduationCap className="w-3.5 h-3.5" /> WHERE ENGINEERING LEADS
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-rsc-navy mb-2">
              From Maker to <span className="text-rsc-yellow">Engineer</span>
            </h2>
            <p className="text-gray-500 max-w-lg mx-auto text-sm">
              Engineering is the highest-paying college major. Every build today is a step toward that career.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            {CAREER_PATHS.map((career, i) => (
              <div key={i} className="p-4 rounded-xl bg-white border border-gray-200 text-center shadow-sm hover:shadow-md transition-all" data-testid={`career-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-rsc-yellow/10 flex items-center justify-center mx-auto mb-2">
                  <career.icon className="w-5 h-5 text-rsc-yellow" />
                </div>
                <h3 className="text-sm font-bold text-rsc-navy mb-0.5">{career.title}</h3>
                <p className="text-rsc-yellow font-bold text-sm mb-1">{career.salary}</p>
                <p className="text-[10px] text-gray-400">{career.prep}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl bg-indigo-50 border border-indigo-200" data-testid="ap-pathway">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-rsc-navy">AP & College Prep Pathway</h3>
                  <p className="text-xs text-indigo-500">Build the foundation now, excel in engineering school later</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { ap: 'AP Physics C', prep: 'Mechanical Engineering + Forces' },
                  { ap: 'AP Calculus', prep: 'Design + Structural Analysis' },
                  { ap: 'Engineering Portfolio', prep: 'All CAD + 3D Print projects' },
                  { ap: 'College Admissions', prep: 'Science Olympiad awards + CAD' },
                ].map((item, i) => (
                  <div key={i} className="p-2 rounded-lg bg-white border border-indigo-100">
                    <p className="text-xs font-semibold text-rsc-navy">{item.ap}</p>
                    <p className="text-[10px] text-gray-400">{item.prep}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-5 rounded-xl bg-amber-50 border border-amber-200" data-testid="competition-pathway">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-rsc-navy">Competition & Build Events</h3>
                  <p className="text-xs text-amber-600">Our programs directly prepare for these competitions</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { event: 'Science Olympiad: Bridges', prep: 'Structural Engineering' },
                  { event: 'Science Olympiad: Machines', prep: 'Mechanical Engineering' },
                  { event: 'FIRST Robotics', prep: 'CAD + Mechanical + Fabrication' },
                  { event: 'Maker Faires', prep: '3D Printing + Fabrication' },
                  { event: 'Architecture Competitions', prep: 'Architecture & Design' },
                  { event: 'CAD Design Challenges', prep: 'TinkerCAD + Fusion 360' },
                ].map((item, i) => (
                  <div key={i} className="p-2 rounded-lg bg-white border border-amber-100">
                    <p className="text-xs font-semibold text-rsc-navy">{item.event}</p>
                    <p className="text-[10px] text-gray-400">{item.prep}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ MAKER PROMISE ═══════════ */}
      <section className="relative py-12 bg-white" data-testid="maker-promise-section">
        <div className="container-app">
          <div className="rounded-2xl bg-gray-50 border border-gray-200 p-8 sm:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-navy/20 bg-rsc-navy/5 text-rsc-navy text-xs font-medium mb-3">
                  <Hammer className="w-3.5 h-3.5" /> OUR MAKER PROMISE
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-rsc-navy mb-3">
                  AI Can't Build <span className="text-rsc-yellow">Physical Things.</span>
                </h2>
                <p className="text-sm text-gray-500 mb-5 leading-relaxed">
                  AI generates code and images, but it can't design a bridge that holds weight,
                  3D print a functional part, or build a robot mechanism. Engineering is hands-on
                  — and hands-on is irreplaceable.
                </p>
                <div className="space-y-2">
                  {[
                    { icon: Printer, text: 'Every student gets their 3D prints shipped home' },
                    { icon: Wrench, text: 'Real tools and materials in every kit' },
                    { icon: Hammer, text: 'Build, test, break, and iterate — real engineering' },
                    { icon: Award, text: 'STEM.org accredited curriculum (Top 5%)' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <item.icon className="w-4 h-4 text-rsc-yellow flex-shrink-0" />
                      <span className="text-sm text-gray-600">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'CAD Software', icon: PenTool, color: 'bg-blue-50 text-blue-600' },
                  { label: '3D Printers', icon: Printer, color: 'bg-orange-50 text-orange-600' },
                  { label: 'Balsa & Materials', icon: Building2, color: 'bg-cyan-50 text-cyan-600' },
                  { label: 'Gear & Pulley Sets', icon: Cog, color: 'bg-red-50 text-red-600' },
                  { label: 'Design Sketchbooks', icon: PenTool, color: 'bg-purple-50 text-purple-600' },
                  { label: 'Safety Equipment', icon: Shield, color: 'bg-amber-50 text-amber-600' },
                ].map((kit, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white border border-gray-200 flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg ${kit.color.split(' ')[0]} flex items-center justify-center flex-shrink-0`}>
                      <kit.icon className={`w-4 h-4 ${kit.color.split(' ')[1]}`} />
                    </div>
                    <span className="text-xs text-gray-600 font-medium">{kit.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ BOTTOM CTA ═══════════ */}
      <section className="relative py-12 bg-[#0f2847]">
        <div className="container-app text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
            Build something real. Become an engineer.
          </h2>
          <p className="text-sm text-white/45 mb-5 max-w-md mx-auto">
            CAD design, 3D printing, and hands-on building for grades 3–12. Real tools. Real projects. Real career paths.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/catalog?category=engineering">
              <Button className="bg-rsc-yellow text-rsc-navy text-sm px-8 py-5 font-bold rounded-full shadow-lg shadow-rsc-yellow/20" data-testid="bottom-cta-courses">
                Explore Courses <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
